// Browser notifications for Supervisors (architecture sprint-14 §8.5, Decision 2: not for Admins). Mounted in
// AppShell only when me.access.checkinNotify. One OS notification per event id: the seen set lives in
// localStorage (≤ 200 ids, FIFO) and the notification tag is the event id, so several tabs collapse to one.
// "Not armed" never notifies; it only counts on the badge.
import type { CheckinAlertProblemDto } from '@shared/alarm-rules';
import { useEffect } from 'react';
import { useCheckinAlerts } from '@/hooks/use-checkin-alerts';
import { useMe } from '@/hooks/use-me';
import { addSeenAlerts, readSeenAlerts } from '@/lib/alarm-store';
import { showAlertNotification } from '@/lib/notifications';
import { clockIn } from '@/lib/shift-format';

export function alertNotificationText(p: CheckinAlertProblemDto, tz: string): { title: string; body: string } | null {
  if (p.kind === 'missed') return { title: 'Missed check-in', body: `${p.user.name} · ${p.projectName} · ${clockIn(p.scheduledAt ?? p.since, tz)}` };
  if (p.kind === 'app_not_running') return { title: 'Clockit not running', body: `${p.user.name} · ${p.projectName} · since ${clockIn(p.since, tz)}` };
  return null;
}

export function CheckinAlertsNotifier(): null {
  const { data } = useCheckinAlerts();
  const { data: me } = useMe();
  const tz = me?.timezone ?? 'UTC';
  useEffect(() => {
    if (!data?.notify) return;
    const seen = new Set(readSeenAlerts());
    const fresh: string[] = [];
    for (const p of data.problems) {
      if (seen.has(p.eventId)) continue;
      const text = alertNotificationText(p, tz);
      if (!text) continue;
      showAlertNotification(p.eventId, text.title, text.body);
      fresh.push(p.eventId);
    }
    if (fresh.length > 0) addSeenAlerts(fresh);
  }, [data, tz]);
  return null;
}
